/**
 * ALL GraphQL for the governance module.
 *
 * Centralized-queries rule: no `gql` tag anywhere else in this folder.
 */
import { gql } from "@apollo/client";

// The laboratory has its own loopback-only schema. These operations are never sent to the tenant API.
export const GOV_LAB_FIELDS = gql`
  fragment GovLabFields on GovLabSnapshot {
    revision mode initialBudget available reserved issued redeemed recurringSpecId recurringSourceId recurringCap recurringPaused mandateApproved
    people { id name role active }
    organisations { id name kind parentId mandate legalStatus }
    work { id title criteria amount period specId status workerId authorisationId acceptanceId reservation proofs { id note submittedBy at } }
    ballots { id kind workId proofId title eligibleIds quorumPercent passPercent status closedAt votes { memberId choice note at } }
    receipts { id workId proofId authorisationId acceptanceId issuedTo ownerId amount unit issuedAt issuedBy status terms originJson originHash }
    events { id actorId action targetId note at }
    applications { id name reason status }
  }
`;
export const GET_GOV_LAB = gql`query GovLabState { govLabState { ...GovLabFields } } ${GOV_LAB_FIELDS}`;
export const RUN_GOV_LAB = gql`mutation RunGovLabCommand($input: GovLabCommand!) { runGovLabCommand(input: $input) { ...GovLabFields } } ${GOV_LAB_FIELDS}`;
export const RESET_GOV_LAB = gql`mutation ResetGovLab($revision: Int!, $confirmation: String!) { resetGovLab(revision: $revision, confirmation: $confirmation) { ...GovLabFields } } ${GOV_LAB_FIELDS}`;

const ORG_CORE = gql`
  fragment GovOrgCore on GovOrganisation {
    id name slug purpose status legalWrapper registrationNumber timezone
    defaultQuorumPercent defaultPassThresholdPercent defaultVotingHours sponsorsRequired rulesVersion
    tokenSymbol tokenKind tokenJourneyMarkdown treasuryPolicyMarkdown rulesMarkdown
    publicPageEnabled publicMemberList publicProof
    snapshotSpaceEns safeAddress chainId createdAt
    draftModel
    linkTeasersEnabled linkTeaserLevel requireSignedBallots digestDayOfWeek
    participationDefaults {
      emailEnabled smsEnabled digestFrequency
      notifyOnProposalOpened notifyOnProposalClosed notifyOnClosingSoon
      notifyOnMemberJoined notifyOnWorkOpened notifyOnDelegateVoted
    }
  }
`;

export const GET_GOV_ORGANISATIONS = gql`
  ${ORG_CORE}
  query GovOrganisations { govOrganisations { ...GovOrgCore } }
`;

export const GET_GOV_ORGANISATION = gql`
  ${ORG_CORE}
  query GovOrganisation($id: ID!) { govOrganisation(id: $id) { ...GovOrgCore } }
`;

export const CREATE_GOV_ORGANISATION = gql`
  ${ORG_CORE}
  mutation CreateGovOrganisation($input: CreateGovOrganisationInput!) {
    createGovOrganisation(input: $input) { ...GovOrgCore }
  }
`;

export const UPDATE_GOV_ORGANISATION = gql`
  ${ORG_CORE}
  mutation UpdateGovOrganisation($id: ID!, $input: UpdateGovOrganisationInput!, $changeNote: String) {
    updateGovOrganisation(id: $id, input: $input, changeNote: $changeNote) { ...GovOrgCore }
  }
`;

export const GET_GOV_ACTIVE_MEMBER_COUNT = gql`
  query GovActiveMemberCount($organisationId: ID!) {
    govActiveMemberCount(organisationId: $organisationId)
  }
`;

export const GET_GOV_ACTIVITY = gql`
  query GovActivity($organisationId: ID!, $kind: String, $limit: Int) {
    govActivity(organisationId: $organisationId, kind: $kind, limit: $limit) {
      id kind summary actorName subjectId subjectType createdAt
      attachments { fileId cid fileName mimeType size url }
    }
  }
`;

// ---- comments and files ON the record (both purely additive) ----

export const GET_GOV_ACTIVITY_COMMENTS = gql`
  query GovActivityComments($organisationId: ID!, $limit: Int) {
    govActivityComments(organisationId: $organisationId, limit: $limit) {
      id activityId subjectType subjectId body authorName authorMemberId authorClientId
      retractedAt retractedByName createdAt
      attachments { fileId cid fileName mimeType size url }
    }
  }
`;

export const ATTACH_GOV_FILE_TO_ACTIVITY = gql`
  mutation AttachGovFileToActivity($input: AttachGovFileInput!) {
    attachGovFileToActivity(input: $input) {
      id kind summary actorName createdAt
      attachments { fileId cid fileName mimeType size url }
      mentions { clientId name source notifiedEmail notifiedSms notifiedAt }
    }
  }
`;

export const ADD_GOV_ACTIVITY_COMMENT = gql`
  mutation AddGovActivityComment($input: AddGovActivityCommentInput!) {
    addGovActivityComment(input: $input) {
      id activityId subjectType subjectId body authorName createdAt
      attachments { fileId cid fileName mimeType size url }
      mentions { clientId name source notifiedEmail notifiedSms notifiedAt }
    }
  }
`;

export const RETRACT_GOV_ACTIVITY_COMMENT = gql`
  mutation RetractGovActivityComment($id: ID!) {
    retractGovActivityComment(id: $id)
  }
`;

// ==================== MEMBERS ====================

export const GET_GOV_MEMBERS = gql`
  query GovMembers($organisationId: ID!, $status: String) {
    govMembers(organisationId: $organisationId, status: $status) {
      id clientId displayName role status joinedAt votingWeight walletAddress sponsoredByMemberId
    }
  }
`;

export const GET_MY_GOV_MEMBERSHIP = gql`
  query MyGovMembership($organisationId: ID!) {
    myGovMembership(organisationId: $organisationId) {
      id displayName role status votingWeight
    }
  }
`;

export const ADD_GOV_MEMBER = gql`
  mutation AddGovMember($input: CreateGovMemberInput!) {
    addGovMember(input: $input) { id displayName role status votingWeight }
  }
`;

export const UPDATE_GOV_MEMBER = gql`
  mutation UpdateGovMember($id: ID!, $input: UpdateGovMemberInput!) {
    updateGovMember(id: $id, input: $input) { id displayName role status votingWeight }
  }
`;

export const GET_GOV_APPLICATIONS = gql`
  query GovApplications($organisationId: ID!, $status: String) {
    govApplications(organisationId: $organisationId, status: $status) {
      id applicantName applicantEmail statement clientId
      sponsorMemberIds sponsorsRequired status decisionNote decidedAt memberId createdAt
    }
  }
`;

export const CREATE_GOV_APPLICATION = gql`
  mutation CreateGovApplication($input: CreateGovApplicationInput!) {
    createGovApplication(input: $input) { id applicantName status sponsorsRequired }
  }
`;

export const SPONSOR_GOV_APPLICATION = gql`
  mutation SponsorGovApplication($id: ID!) {
    sponsorGovApplication(id: $id) { id sponsorMemberIds sponsorsRequired status }
  }
`;

export const DECIDE_GOV_APPLICATION = gql`
  mutation DecideGovApplication($id: ID!, $accept: Boolean!, $note: String) {
    decideGovApplication(id: $id, accept: $accept, note: $note) { id status memberId decisionNote }
  }
`;

// ==================== PROPOSALS ====================

const PROPOSAL_CORE = gql`
  fragment GovProposalCore on GovProposal {
    id organisationId title body type votingSystem status
    choices { key label }
    quorumPercent passThresholdPercent eligibleWeightAtOpen eligibleCountAtOpen
    opensAt closesAt closedAt sopEpisodeId proposedByName amount amountUnit createdAt
    citedActivityIds supersedesProposalId supersededByProposalId
    attachments { fileId cid fileName mimeType size url }
  }
`;

export const GET_GOV_PROPOSALS = gql`
  ${PROPOSAL_CORE}
  query GovProposals($organisationId: ID!, $status: String) {
    govProposals(organisationId: $organisationId, status: $status) { ...GovProposalCore }
  }
`;

export const GET_GOV_PROPOSAL = gql`
  ${PROPOSAL_CORE}
  query GovProposal($id: ID!) { govProposal(id: $id) { ...GovProposalCore } }
`;

export const GET_GOV_PROPOSAL_TALLY = gql`
  query GovProposalTally($proposalId: ID!) {
    govProposalTally(proposalId: $proposalId) {
      choices { key label weight votes percentOfCast }
      totalWeightCast totalVotesCast turnoutPercent quorumMet
      leadingChoiceKey tied provisionalOutcome provisional
    }
  }
`;

export const GET_MY_GOV_VOTE = gql`
  query MyGovVote($proposalId: ID!) {
    myGovVote(proposalId: $proposalId) { id choiceKeys weight castAt reason viaDelegation castByMemberId }
  }
`;

export const GET_GOV_VOTES = gql`
  query GovVotes($proposalId: ID!) {
    govVotes(proposalId: $proposalId) { id memberId memberName choiceKeys weight castAt reason viaDelegation castByMemberId }
  }
`;

export const CREATE_GOV_PROPOSAL = gql`
  ${PROPOSAL_CORE}
  mutation CreateGovProposal($input: CreateGovProposalInput!) {
    createGovProposal(input: $input) { ...GovProposalCore }
  }
`;

export const OPEN_GOV_PROPOSAL = gql`
  ${PROPOSAL_CORE}
  mutation OpenGovProposal($id: ID!, $closesAt: DateTime) {
    openGovProposal(id: $id, closesAt: $closesAt) { ...GovProposalCore }
  }
`;

export const CAST_GOV_VOTE = gql`
  mutation CastGovVote($input: CastGovVoteInput!) {
    castGovVote(input: $input) { id choiceKeys weight castAt signature signerAddress }
  }
`;

export const CLOSE_GOV_PROPOSAL = gql`
  mutation CloseGovProposal($id: ID!) {
    closeGovProposal(id: $id) {
      id title outcome winningChoiceKey totalWeightCast totalVotesCast decidedAt
    }
  }
`;

export const CANCEL_GOV_PROPOSAL = gql`
  mutation CancelGovProposal($id: ID!, $reason: String) {
    cancelGovProposal(id: $id, reason: $reason) { id status }
  }
`;

/** Put a finished motion again — a NEW draft, linked both ways. There is deliberately no reopen. */
export const REPUT_GOV_PROPOSAL = gql`
  ${PROPOSAL_CORE}
  mutation ReputGovProposal($id: ID!, $reason: String) {
    reputGovProposal(id: $id, reason: $reason) { ...GovProposalCore }
  }
`;

export const GET_GOV_DECISIONS = gql`
  query GovDecisions($organisationId: ID!) {
    govDecisions(organisationId: $organisationId) {
      id proposalId title outcome winningChoiceKey totalWeightCast totalVotesCast
      decidedAt sopEpisodeId executionNote executionTxHash
    }
  }
`;

export const RECORD_GOV_EXECUTION = gql`
  mutation RecordGovExecution($decisionId: ID!, $executionNote: String!, $executionTxHash: String) {
    recordGovExecution(decisionId: $decisionId, executionNote: $executionNote, executionTxHash: $executionTxHash) {
      id executionNote executionTxHash
    }
  }
`;

// ==================== WORK ====================

export const GET_GOV_WORK_ITEMS = gql`
  query GovWorkItems($organisationId: ID!, $status: String) {
    govWorkItems(organisationId: $organisationId, status: $status) {
      id title brief status rewardAmount rewardUnit dueDate maxClaimants decisionId createdAt
    }
  }
`;

export const GET_GOV_WORK_ITEM = gql`
  query GovWorkItem($id: ID!) {
    govWorkItem(id: $id) {
      id organisationId title brief status rewardAmount rewardUnit dueDate maxClaimants decisionId
    }
  }
`;

export const GET_GOV_WORK_CLAIMS = gql`
  query GovWorkClaims($workItemId: ID!) {
    govWorkClaims(workItemId: $workItemId) { id memberId memberName status claimedAt releasedAt note }
  }
`;

export const GET_GOV_WORK_PROOFS = gql`
  query GovWorkProofs($workItemId: ID!) {
    govWorkProofs(workItemId: $workItemId) {
      id memberId memberName note status reviewNote reviewedAt reportedAtEpisodeId createdAt
      files { cid fileName mimeType size url }
    }
  }
`;

export const CREATE_GOV_WORK_ITEM = gql`
  mutation CreateGovWorkItem($input: CreateGovWorkItemInput!) {
    createGovWorkItem(input: $input) { id title status rewardAmount rewardUnit }
  }
`;

export const CLAIM_GOV_WORK = gql`
  mutation ClaimGovWork($workItemId: ID!, $note: String) {
    claimGovWork(workItemId: $workItemId, note: $note) { id memberName status claimedAt }
  }
`;

export const RELEASE_GOV_WORK_CLAIM = gql`
  mutation ReleaseGovWorkClaim($claimId: ID!, $note: String) {
    releaseGovWorkClaim(claimId: $claimId, note: $note)
  }
`;

export const SUBMIT_GOV_PROOF = gql`
  mutation SubmitGovProof($input: SubmitGovProofInput!) {
    submitGovProof(input: $input) { id status createdAt }
  }
`;

export const REVIEW_GOV_PROOF = gql`
  mutation ReviewGovProof($proofId: ID!, $input: ReviewGovProofInput!) {
    reviewGovProof(proofId: $proofId, input: $input) { id status reviewNote reviewedAt }
  }
`;

export const GRANT_GOV_TOKENS = gql`
  mutation GrantGovTokens($workItemId: ID!, $memberId: ID!, $amount: Float!, $reason: String!) {
    grantGovTokens(workItemId: $workItemId, memberId: $memberId, amount: $amount, reason: $reason) {
      id amount unit reason memberName createdAt
    }
  }
`;

export const GET_GOV_TOKEN_GRANTS = gql`
  query GovTokenGrants($organisationId: ID!) {
    govTokenGrants(organisationId: $organisationId) {
      id memberName amount unit reason workItemId createdAt
    }
  }
`;

// ==================== TREASURY ====================

export const GET_GOV_TREASURY_ACCOUNTS = gql`
  query GovTreasuryAccounts($organisationId: ID!) {
    govTreasuryAccounts(organisationId: $organisationId) {
      id label kind identifier currency balance balanceSource balanceAsAt isActive
    }
  }
`;

export const GET_GOV_TREASURY_MOVEMENTS = gql`
  query GovTreasuryMovements($organisationId: ID!) {
    govTreasuryMovements(organisationId: $organisationId) {
      id accountId direction amount currency description decisionId workItemId occurredAt
    }
  }
`;

export const CREATE_GOV_TREASURY_ACCOUNT = gql`
  mutation CreateGovTreasuryAccount($input: CreateGovAccountInput!) {
    createGovTreasuryAccount(input: $input) { id label kind balance currency }
  }
`;

export const SET_GOV_ACCOUNT_BALANCE = gql`
  mutation SetGovAccountBalance($accountId: ID!, $balance: Float!, $asAt: DateTime) {
    setGovAccountBalance(accountId: $accountId, balance: $balance, asAt: $asAt) {
      id balance balanceSource balanceAsAt
    }
  }
`;

export const RECORD_GOV_MOVEMENT = gql`
  mutation RecordGovMovement($input: RecordGovMovementInput!) {
    recordGovMovement(input: $input) { id direction amount description occurredAt }
  }
`;

// ==================== FILE UPLOAD ====================
//
// The platform's ONE browser upload path — the chunked `mediaCDN*` family, the
// same one vlog and the employee module drive. Declared HERE rather than
// imported from another module's queries file: a cross-module import is exactly
// what Module Sync cannot carry.
//
// Nobody types a CID. You drop a file, it uploads, and the CID comes back.

export const GOV_UPLOAD_INITIATE = gql`
  mutation GovMediaCDNInitiateUpload($fileName: String!, $totalChunks: Float!, $fileSize: Float!) {
    mediaCDNInitiateUpload(fileName: $fileName, totalChunks: $totalChunks, fileSize: $fileSize)
  }
`;

export const GOV_UPLOAD_CHUNK = gql`
  mutation GovMediaCDNUploadChunk($uploadId: String!, $chunkNumber: Float!, $chunk: Upload!) {
    mediaCDNUploadChunk(uploadId: $uploadId, chunkNumber: $chunkNumber, chunk: $chunk)
  }
`;

export const GOV_UPLOAD_COMPLETE = gql`
  mutation GovMediaCDNCompleteUpload($uploadId: String!) {
    mediaCDNCompleteUpload(uploadId: $uploadId) {
      ipfsHash
      url
      fileSize
      mimeType
      fileName
    }
  }
`;

export const GOV_UPLOAD_CANCEL = gql`
  mutation GovMediaCDNCancelUpload($uploadId: String!) {
    mediaCDNCancelUpload(uploadId: $uploadId)
  }
`;

export const GOV_UPLOAD_GET_PROGRESS = gql`
  mutation GovMediaCDNGetUploadProgress($uploadId: String!) {
    mediaCDNGetUploadProgress(uploadId: $uploadId)
  }
`;

// ==================== NOTIFICATION RULES ====================

export const GET_GOV_NOTIFICATION_RULES = gql`
  query GovNotificationRules($organisationId: ID!) {
    govNotificationRules(organisationId: $organisationId) {
      id name trigger recipientType recipientEmails channels
      hoursBefore isActive customMessage lastFiredAt timesFired createdAt
    }
  }
`;

export const CREATE_GOV_NOTIFICATION_RULE = gql`
  mutation CreateGovNotificationRule($input: CreateGovNotificationRuleInput!) {
    createGovNotificationRule(input: $input) {
      id name trigger recipientType channels isActive
    }
  }
`;

export const UPDATE_GOV_NOTIFICATION_RULE = gql`
  mutation UpdateGovNotificationRule($id: ID!, $input: UpdateGovNotificationRuleInput!) {
    updateGovNotificationRule(id: $id, input: $input) {
      id name recipientType channels isActive customMessage hoursBefore
    }
  }
`;

export const DELETE_GOV_NOTIFICATION_RULE = gql`
  mutation DeleteGovNotificationRule($id: ID!) {
    deleteGovNotificationRule(id: $id)
  }
`;

// ==================== INVITES ====================

const INVITE_FIELDS = `
  id slug organisationId email fName lName invitedByName invitedByMemberId
  countsAsSponsorship status customMessage emailSentAt sendError
  viewedAt respondedAt joinedAt expiresAt createdAt
`;

export const GET_GOV_INVITES = gql`
  query GovInvites($organisationId: ID!) {
    govInvites(organisationId: $organisationId) { ${INVITE_FIELDS} }
  }
`;

export const GET_GOV_INVITE_GRAPH = gql`
  query GovInviteGraph($organisationId: ID!) {
    govInviteGraph(organisationId: $organisationId) {
      id name invitedByName invitedByMemberId status joinedAt
    }
  }
`;

export const CREATE_GOV_INVITE = gql`
  mutation CreateGovInvite($input: CreateGovInviteInput!) {
    createGovInvite(input: $input) { ${INVITE_FIELDS} }
  }
`;

/**
 * The site's client list, for "invite someone already on your client list" (2026-09-11, the
 * Vibe Tribe pattern). The server gates it on client access, so a governance member without it
 * gets an error — the invite dialog uses that to hide the option rather than show a dead one.
 * Module-local copy (never import another module's queries).
 */
export const GOV_SEARCH_CLIENTS = gql`
  query GovSearchClients($search: String, $tags: [String!], $limit: Int) {
    searchClients(search: $search, tags: $tags, limit: $limit) { id fName lName email tags }
  }
`;

export const RESEND_GOV_INVITE = gql`
  mutation ResendGovInvite($id: ID!) {
    resendGovInvite(id: $id) { ${INVITE_FIELDS} }
  }
`;

export const REVOKE_GOV_INVITE = gql`
  mutation RevokeGovInvite($id: ID!) {
    revokeGovInvite(id: $id)
  }
`;

// ---- public (no login) ----

export const GET_PUBLIC_GOV_INVITE = gql`
  query PublicGovInvite($slug: String!) {
    publicGovInvite(slug: $slug) {
      slug organisationName organisationPurpose organisationRules rulesVersion rulesChangedAt invitedByName inviteeFName
      customMessage status sponsorsRequired countsAsSponsorship expiresAt closedReason
    }
  }
`;

/**
 * The public organisation page. NO login, and deliberately under-exposed —
 * the backend decides what a stranger may see (see
 * `govPublicOrg.resolver.ts`); this asks only for what it publishes.
 * There is no member email, no referral graph and no open ballot here.
 */
export const GET_PUBLIC_GOV_ORGANISATION = gql`
  query PublicGovOrganisation($slug: String!) {
    publicGovOrganisation(slug: $slug) {
      slug name purpose rulesMarkdown rulesVersion rulesChangedAt legalWrapper registrationNumber tokenSymbol timezone
      memberCount openProposalCount memberListPublished
      members { name role }
      decisions { id title outcome winningChoiceLabel totalVotesCast totalWeightCast decidedAt decidedAtLabel }
      decisionsTotal decisionsCap
    }
  }
`;

export const SET_GOV_ORGANISATION_PUBLIC_PAGE = gql`
  mutation SetGovOrganisationPublicPage($id: ID!, $enabled: Boolean!, $publicMemberList: Boolean) {
    setGovOrganisationPublicPage(id: $id, enabled: $enabled, publicMemberList: $publicMemberList) {
      id slug publicPageEnabled publicMemberList
    }
  }
`;

/** Runs the closing-soon sweep now and reports what the mail service confirmed. */
export const SWEEP_GOV_VOTE_REMINDERS = gql`
  mutation SweepGovVoteReminders($organisationId: ID!) {
    sweepGovVoteReminders(organisationId: $organisationId) {
      proposalsChecked remindersFired sent failed summary
    }
  }
`;

/** The caps the list queries apply, served so a page can state them honestly. */
export const GET_GOV_LIST_CAPS = gql`
  query GovListCaps {
    govListCaps { proposals decisions }
  }
`;

export const ACCEPT_GOV_INVITE = gql`
  mutation AcceptGovInvite($input: AcceptGovInviteInput!) {
    acceptGovInvite(input: $input) { ok message applicationId sponsorsStillNeeded }
  }
`;

export const DECLINE_GOV_INVITE = gql`
  mutation DeclineGovInvite($slug: String!) {
    declineGovInvite(slug: $slug)
  }
`;

// ==================== TALK-IT-THROUGH PROPOSAL DRAFTING ====================

export const UPLOAD_GOV_BRIEF_AUDIO = gql`
  mutation UploadGovBriefAudio($mimeType: String!, $base64: String!) {
    uploadGovBriefAudio(mimeType: $mimeType, base64: $base64)
  }
`;

export const TRANSCRIBE_GOV_BRIEF = gql`
  mutation TranscribeGovBrief($audioCid: String!) {
    transcribeGovBrief(audioCid: $audioCid) { text model reason }
  }
`;

export const DRAFT_GOV_PROPOSAL = gql`
  mutation DraftGovProposal($input: DraftGovProposalInput!) {
    draftGovProposal(input: $input) {
      title bodyMarkdown suggestedType amount decisionStatement openQuestions model
      choices { key label }
    }
  }
`;

// ==================== JOB SPECIFICATIONS ====================

const JOBSPEC_CORE = gql`
  fragment GovJobSpecCore on GovJobSpec {
    id organisationId familyId version supersedesId
    title summary steps doneLooksLike evidenceRequired
    priceTokens estimatedHours status publicListed
    authoredByName adoptedAt adoptedByProposalId retiredAt
    timesStarted sopEpisodeId createdAt
  }
`;

export const GET_GOV_JOB_SPECS = gql`
  ${JOBSPEC_CORE}
  query GovJobSpecs($organisationId: ID!, $status: String) {
    govJobSpecs(organisationId: $organisationId, status: $status) { ...GovJobSpecCore }
  }
`;

export const GET_GOV_JOB_MARKETPLACE = gql`
  ${JOBSPEC_CORE}
  query GovJobMarketplace($limit: Int) {
    govJobMarketplace(limit: $limit) { ...GovJobSpecCore }
  }
`;

export const CREATE_GOV_JOB_SPEC = gql`
  ${JOBSPEC_CORE}
  mutation CreateGovJobSpec($input: CreateGovJobSpecInput!) {
    createGovJobSpec(input: $input) { ...GovJobSpecCore }
  }
`;

export const UPDATE_GOV_JOB_SPEC = gql`
  ${JOBSPEC_CORE}
  mutation UpdateGovJobSpec($id: ID!, $input: UpdateGovJobSpecInput!) {
    updateGovJobSpec(id: $id, input: $input) { ...GovJobSpecCore }
  }
`;

export const PROPOSE_GOV_JOB_SPEC = gql`
  ${JOBSPEC_CORE}
  mutation ProposeGovJobSpec($id: ID!) {
    proposeGovJobSpec(id: $id) { ...GovJobSpecCore }
  }
`;

export const ADOPT_GOV_JOB_SPEC = gql`
  ${JOBSPEC_CORE}
  mutation AdoptGovJobSpec($id: ID!) {
    adoptGovJobSpec(id: $id) { ...GovJobSpecCore }
  }
`;

export const REVISE_GOV_JOB_SPEC = gql`
  ${JOBSPEC_CORE}
  mutation ReviseGovJobSpec($id: ID!, $input: UpdateGovJobSpecInput!) {
    reviseGovJobSpec(id: $id, input: $input) { ...GovJobSpecCore }
  }
`;

export const RETIRE_GOV_JOB_SPEC = gql`
  mutation RetireGovJobSpec($id: ID!) { retireGovJobSpec(id: $id) }
`;

export const START_GOV_JOB_SPEC_INSTANCE = gql`
  mutation StartGovJobSpecInstance($input: StartGovJobSpecInstanceInput!) {
    startGovJobSpecInstance(input: $input) {
      id title status rewardAmount rewardUnit jobSpecId jobSpecVersion organisationId
    }
  }
`;

// ==================== EDITING A MOTION ====================

export const GET_GOV_PROPOSAL_REVISIONS = gql`
  query GovProposalRevisions($proposalId: ID!) {
    govProposalRevisions(proposalId: $proposalId) {
      id version title body changeNote proposalStatusAtEdit
      editedDuringVoting votesCastBefore editedByName createdAt
      attachments { fileId cid fileName mimeType size url }
    }
  }
`;

export const UPDATE_GOV_PROPOSAL = gql`
  ${PROPOSAL_CORE}
  mutation UpdateGovProposal($id: ID!, $input: UpdateGovProposalInput!) {
    updateGovProposal(id: $id, input: $input) { ...GovProposalCore }
  }
`;

/**
 * The Sovereign AI backend in effect for this site and the models it serves — read by
 * the settings page's AI panel. Never carries the key; the endpoint is edited on the
 * tenant page. Served by the local-ai module (a prerequisite module).
 */
export const LOCAL_AI_RUNTIME = gql`
  query LocalAiRuntime {
    localAiRuntime {
      source endpointHost defaultModel ok error
      models { id role slots }
    }
  }
`;

// ==================== PARTICIPATION (v0.2) ====================

const PARTICIPATION_CORE = gql`
  fragment GovParticipationCore on GovParticipation {
    id organisationId clientId displayName level delegateMemberId delegatedAt
    emailEnabled smsEnabled digestFrequency lastDigestSentAt
    notifyOnProposalOpened notifyOnProposalClosed notifyOnClosingSoon
    notifyOnMemberJoined notifyOnWorkOpened notifyOnDelegateVoted
    lastUpdatedBySelf selfSetAt adminOverrodeAt updatedAt
  }
`;

const SCHEDULE_ROW = gql`
  fragment GovScheduleRow on GovNotificationScheduleRow {
    kind label description on email sms frequencyLabel field orgOff
  }
`;

/** Auth-only: is the caller allowed the full page, and what is their standing here? */
export const GET_MY_GOV_ACCESS = gql`
  ${PARTICIPATION_CORE}
  query MyGovAccess($organisationId: ID!) {
    myGovAccess(organisationId: $organisationId) {
      canView isMember level
      membership { id displayName role status votingWeight }
      participation { ...GovParticipationCore }
    }
  }
`;

/** Auth-only: the front door — the public whitelist, the roster by name and role, and the caller's own row. */
export const GET_GOV_ORGANISATION_ENTRY = gql`
  ${PARTICIPATION_CORE}
  query GovOrganisationEntry($id: ID!) {
    govOrganisationEntry(id: $id) {
      id name slug purpose rulesMarkdown rulesVersion rulesChangedAt legalWrapper timezone sponsorsRequired
      memberCount openProposalCount
      roster { memberId name role delegationsHeld }
      access {
        canView isMember level
        membership { id displayName role status votingWeight }
        participation { ...GovParticipationCore }
      }
    }
  }
`;

export const GET_MY_GOV_PARTICIPATION = gql`
  ${PARTICIPATION_CORE}
  query MyGovParticipation($organisationId: ID!) {
    myGovParticipation(organisationId: $organisationId) { ...GovParticipationCore }
  }
`;

export const GET_MY_GOV_NOTIFICATION_SCHEDULE = gql`
  ${SCHEDULE_ROW}
  query MyGovNotificationSchedule($organisationId: ID!) {
    myGovNotificationSchedule(organisationId: $organisationId) { ...GovScheduleRow }
  }
`;

export const GET_GOV_DEFAULT_NOTIFICATION_SCHEDULE = gql`
  ${SCHEDULE_ROW}
  query GovDefaultNotificationSchedule($organisationId: ID!) {
    govDefaultNotificationSchedule(organisationId: $organisationId) { ...GovScheduleRow }
  }
`;

export const GET_GOV_DELEGATION_COUNTS = gql`
  query GovDelegationCounts($organisationId: ID!) {
    govDelegationCounts(organisationId: $organisationId) { memberId name count }
  }
`;

export const GET_GOV_PARTICIPANTS = gql`
  ${PARTICIPATION_CORE}
  query GovParticipants($organisationId: ID!) {
    govParticipants(organisationId: $organisationId) { ...GovParticipationCore }
  }
`;

export const UPDATE_MY_GOV_PARTICIPATION = gql`
  ${PARTICIPATION_CORE}
  mutation UpdateMyGovParticipation($organisationId: ID!, $input: UpdateGovParticipationInput!) {
    updateMyGovParticipation(organisationId: $organisationId, input: $input) { ...GovParticipationCore }
  }
`;

export const SET_GOV_DELEGATE = gql`
  ${PARTICIPATION_CORE}
  mutation SetGovDelegate($organisationId: ID!, $delegateMemberId: ID) {
    setGovDelegate(organisationId: $organisationId, delegateMemberId: $delegateMemberId) { ...GovParticipationCore }
  }
`;

export const UPDATE_GOV_PARTICIPATION_AS_ADMIN = gql`
  ${PARTICIPATION_CORE}
  mutation UpdateGovParticipationAsAdmin($organisationId: ID!, $clientId: ID!, $input: UpdateGovParticipationInput!, $overrideSelfSet: Boolean) {
    updateGovParticipationAsAdmin(organisationId: $organisationId, clientId: $clientId, input: $input, overrideSelfSet: $overrideSelfSet) { ...GovParticipationCore }
  }
`;

/** Runs the weekly / fortnightly report sweep now and reports what the server confirmed. */
export const SEND_GOV_DUE_REPORTS = gql`
  mutation SendGovDueReports($organisationId: ID!, $dryRun: Boolean) {
    sendGovDueReports(organisationId: $organisationId, dryRun: $dryRun) {
      candidates sent failed skipped dryRun summary
    }
  }
`;

/** Link previews: whether a forwarded motion link shows its title and opening lines before sign-in. */
export const SET_GOV_ORGANISATION_LINK_TEASERS = gql`
  mutation SetGovOrganisationLinkTeasers($id: ID!, $enabled: Boolean!) {
    setGovOrganisationLinkTeasers(id: $id, enabled: $enabled) { id linkTeasersEnabled }
  }
`;

/**
 * PUBLIC — no auth. The written teaser a stranger sees on a forwarded motion link.
 * Served only when the organisation has link previews on; never choices, tally,
 * votes, attachments or the proposer.
 */
export const GET_PUBLIC_GOV_PROPOSAL_TEASER = gql`
  query PublicGovProposalTeaser($id: ID!) {
    publicGovProposalTeaser(id: $id) {
      organisationId organisationName organisationSlug title status closesAtLabel excerpt membersCount
    }
  }
`;

// ==================== ACTIVITY (paged + notes) ====================

export const GET_GOV_ACTIVITY_PAGE = gql`
  query GovActivityPage($organisationId: ID!, $kind: String, $limit: Int, $offset: Int) {
    govActivityPage(organisationId: $organisationId, kind: $kind, limit: $limit, offset: $offset) {
      total cap
      rows {
        id kind summary actorName actorClientId subjectId subjectType detailJson createdAt
        attachments { fileId cid fileName mimeType size url }
        mentions { clientId name source notifiedEmail notifiedSms notifiedAt }
      }
    }
  }
`;

export const ADD_GOV_ACTIVITY_NOTE = gql`
  mutation AddGovActivityNote($input: AddGovActivityNoteInput!) {
    addGovActivityNote(input: $input) {
      id kind summary actorName createdAt detailJson
      attachments { fileId cid fileName mimeType size url }
      mentions { clientId name source notifiedEmail notifiedSms notifiedAt }
    }
  }
`;

// ==================== TAGGING PEOPLE (2026-09-15) ====================

/** Who this person may tag here: members always, staff and clients only if they may already see them. */
export const GET_GOV_MENTIONABLE_PEOPLE = gql`
  query GovMentionablePeople($organisationId: ID!, $search: String) {
    govMentionablePeople(organisationId: $organisationId, search: $search) {
      clientId name source roleLabel hasEmail hasPhone
    }
  }
`;

/** ONE entry, in full — what the event's own page reads. Null for someone outside the group. */
export const GET_GOV_ACTIVITY_ENTRY = gql`
  query GovActivityEntry($id: ID!) {
    govActivityEntry(id: $id) {
      id organisationId kind summary actorName actorClientId subjectId subjectType detailJson createdAt
      attachments { fileId cid fileName mimeType size url }
      mentions { clientId name source notifiedEmail notifiedSms notifiedAt }
    }
  }
`;

/** What a link-holder outside the group may see of one event: the group, the kind of event, when. */
/**
 * The WHOLE motion for somebody who is not in the group — only when that group publishes at the FULL
 * level. Never the individual votes, never the papers: the whitelist is pinned by a backend test.
 */
export const GET_PUBLIC_GOV_MOTION = gql`
  query PublicGovMotion($id: ID!) {
    publicGovMotion(id: $id) {
      organisationId organisationName organisationSlug organisationPurpose publicPageEnabled
      id title body status closesAtLabel statusWords passRule
      choices { key label votes percentOfCast }
      totalVotesCast turnoutPercent quorumMet membersCount
    }
  }
`;

/** How much a forwarded motion link shows: NONE · TEASER · FULL. Its own act, logged on the record. */
export const SET_GOV_LINK_TEASER_LEVEL = gql`
  mutation SetGovOrganisationLinkTeaserLevel($id: ID!, $level: GovLinkTeaserLevel!) {
    setGovOrganisationLinkTeaserLevel(id: $id, level: $level) { id linkTeasersEnabled linkTeaserLevel }
  }
`;

export const GET_PUBLIC_GOV_ACTIVITY_TEASER = gql`
  query PublicGovActivityTeaser($id: ID!) {
    publicGovActivityTeaser(id: $id) {
      organisationId organisationName organisationSlug what whenLabel membersCount
    }
  }
`;

/** Tell the people tagged on an entry, on the channels chosen per person. Reports what was sent. */
export const NOTIFY_GOV_MENTIONS = gql`
  mutation NotifyGovMentions($entryId: ID!, $choices: [GovMentionNotifyChoiceInput!]!, $commentId: ID) {
    notifyGovMentions(entryId: $entryId, choices: $choices, commentId: $commentId) {
      summary
      rows { clientId name emailSent smsSent problem overrodePreference }
    }
  }
`;

/** Can this group be deleted, and what goes with it (2026-09-11). Same people who may delete. */
export const GOV_ORGANISATION_DELETE_CHECK = gql`
  query GovOrganisationDeleteCheck($id: ID!) {
    govOrganisationDeleteCheck(id: $id) { deletable blockers removes }
  }
`;

/** Deletes a group that holds no motions, work, tokens, treasury or job specs. The name must match. */
export const DELETE_GOV_ORGANISATION = gql`
  mutation DeleteGovOrganisation($id: ID!, $confirmName: String!) {
    deleteGovOrganisation(id: $id, confirmName: $confirmName) { deletable blockers removes }
  }
`;

// ── Checklists — the group's working state (universal-activity-feed layer 7, 2026-09-14) ─────────
const CHECKLIST_CORE = gql`
  fragment GovChecklistCore on GovChecklist {
    id organisationId subjectType subjectId title isTemplate visibility
    createdById createdByName copiedFromChecklistId lastActivityAt createdAt
  }
`;

export const GET_GOV_CHECKLISTS = gql`
  ${CHECKLIST_CORE}
  query GovChecklists($organisationId: ID!, $subjectId: ID) {
    govChecklists(organisationId: $organisationId, subjectId: $subjectId) { ...GovChecklistCore }
  }
`;

/** Every sub-task for the group's lists in ONE query — the page groups them by `checklistId`. */
export const GET_GOV_CHECKLIST_ITEMS = gql`
  query GovChecklistItems($organisationId: ID!) {
    govChecklistItems(organisationId: $organisationId) {
      id checklistId label order done completedById completedByName completedAt
    }
  }
`;

export const CREATE_GOV_CHECKLIST = gql`
  ${CHECKLIST_CORE}
  mutation CreateGovChecklist($input: CreateGovChecklistInput!) {
    createGovChecklist(input: $input) { ...GovChecklistCore }
  }
`;

export const ADD_GOV_CHECKLIST_ITEM = gql`
  mutation AddGovChecklistItem($input: AddGovChecklistItemInput!) {
    addGovChecklistItem(input: $input) { id checklistId label order done }
  }
`;

export const SET_GOV_CHECKLIST_ITEM_DONE = gql`
  mutation SetGovChecklistItemDone($itemId: ID!, $done: Boolean!) {
    setGovChecklistItemDone(itemId: $itemId, done: $done) {
      id checklistId label order done completedByName completedAt
    }
  }
`;

export const USE_GOV_CHECKLIST_TEMPLATE = gql`
  ${CHECKLIST_CORE}
  mutation UseGovChecklistTemplate($templateId: ID!, $subjectType: String, $subjectId: ID) {
    useGovChecklistTemplate(templateId: $templateId, subjectType: $subjectType, subjectId: $subjectId) { ...GovChecklistCore }
  }
`;

export const SET_GOV_CHECKLIST_VISIBILITY = gql`
  ${CHECKLIST_CORE}
  mutation SetGovChecklistVisibility($input: SetGovChecklistVisibilityInput!) {
    setGovChecklistVisibility(input: $input) { ...GovChecklistCore }
  }
`;

export const REMOVE_GOV_CHECKLIST = gql`
  mutation RemoveGovChecklist($checklistId: ID!) {
    removeGovChecklist(checklistId: $checklistId)
  }
`;

/**
 * ONE record's history — deliberately a separate document from the group's feed (2026-09-14).
 *
 * `subjectId` was added to `govActivityPage` the same day, and a backend that had not restarted
 * refused the whole document, which took the group's entire Activity tab down with it. Split, an
 * older server breaks only this section.
 */
export const GET_GOV_RECORD_HISTORY = gql`
  query GovRecordHistory($organisationId: ID!, $subjectId: ID, $limit: Int, $offset: Int) {
    govActivityPage(organisationId: $organisationId, subjectId: $subjectId, limit: $limit, offset: $offset) {
      total cap
      rows {
        id kind summary actorName actorClientId subjectId subjectType detailJson createdAt
        attachments { fileId cid fileName mimeType size url }
        mentions { clientId name source notifiedEmail notifiedSms notifiedAt }
      }
    }
  }
`;

// ── The rules, version by version (the policies pattern, 2026-09-14) ─────────────────────────────
const RULES_DIFF = gql`
  fragment GovRulesDiffCore on GovRulesDiff {
    fromVersion toVersion added removed identical
    fieldChanges
    changedSections { heading added removed }
    hunks { fromStart toStart lines { kind text fromLine toLine } }
  }
`;

export const GET_GOV_ORGANISATION_REVISIONS = gql`
  query GovOrganisationRevisions($organisationId: ID!) {
    govOrganisationRevisions(organisationId: $organisationId) {
      id version changeNote isStartingPoint changedById changedByName changedFields createdAt
      snapshot {
        name purpose rulesMarkdown tokenSymbol
        defaultQuorumPercent defaultPassThresholdPercent defaultVotingHours sponsorsRequired
      }
    }
  }
`;

export const GET_GOV_REVISION_DIFF = gql`
  ${RULES_DIFF}
  query GovOrganisationRevisionDiff($organisationId: ID!, $fromVersion: Int!, $toVersion: Int!) {
    govOrganisationRevisionDiff(organisationId: $organisationId, fromVersion: $fromVersion, toVersion: $toVersion) {
      ...GovRulesDiffCore
    }
  }
`;

/** What the form in front of you would change, asked before anything is written. */
export const GET_GOV_DRAFT_DIFF = gql`
  ${RULES_DIFF}
  query GovOrganisationDraftDiff($organisationId: ID!, $draft: GovRulesDraftInput!) {
    govOrganisationDraftDiff(organisationId: $organisationId, draft: $draft) { ...GovRulesDiffCore }
  }
`;
